/**
 * RealtimeLoop implementation accumulates real elapsed time between
 * requestAnimationFrame callbacks and invokes onTick once per fixed logical
 * step, so the number of ticks per second stays constant regardless of
 * frame-rate jitter. The caller submits each tick as an ordinary move with no
 * timing data of its own — wall-clock time never enters authoritative game
 * state, only how many fixed steps to advance it by.
 */
import type { RealtimeLoop } from './realtimeLoop.types'

export function makeRealtimeLoop(): RealtimeLoop {
  let rafHandle: number | null = null
  let lastFrameTime: number | null = null
  let accumulatorMs = 0

  function stop(): void {
    if (rafHandle !== null) {
      cancelAnimationFrame(rafHandle)
      rafHandle = null
    }
    lastFrameTime = null
    accumulatorMs = 0
  }

  function start(tickRateHz: number, onTick: () => void): void {
    stop() // idempotent: restarting replaces any prior loop/rate
    const stepMs = 1000 / tickRateHz

    const frame = (now: number) => {
      if (lastFrameTime !== null) {
        accumulatorMs += now - lastFrameTime
        while (accumulatorMs >= stepMs) {
          onTick()
          accumulatorMs -= stepMs
        }
      }
      lastFrameTime = now
      rafHandle = requestAnimationFrame(frame)
    }
    rafHandle = requestAnimationFrame(frame)
  }

  return { start, stop }
}
